import { useEffect, useMemo, useState } from 'react';
import { MEAL_SLOTS, type MealSlot, type PlannedMeal, type RecipeListItem } from '@mlc/shared';
import { clearMeal, listMealPlan, listRecipes, planWeekToShopping, setMeal } from './api';
import { Link, paths } from './router';
import { Banner, Button, Card, Icon, PageHeader, Select } from './ui';

/**
 * Planification hebdomadaire (US-36) : « je veux décider mes repas de la
 * semaine et en tirer ma liste ».
 *
 * Dernier maillon du parcours réel — la bibliothèque dit ce qu'on sait faire,
 * cet écran ce qu'on va faire, la liste de courses ce qu'il faut acheter pour
 * cela. Le bouton qui referme la boucle est donc le plus important de la page.
 *
 * Grille à deux créneaux par jour (midi, soir) et non trois : le
 * petit-déjeuner ne se décide pas la veille, et l'ajouter ferait une grille au
 * tiers vide.
 *
 * Aucune génération automatique : décider ce qu'on mange est le geste que
 * cette application accompagne, pas celui qu'elle remplace.
 */

const DAY_NAMES = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const SLOT_LABEL: Record<MealSlot, string> = { midi: 'Midi', soir: 'Soir' };

/**
 * Lundi de la semaine contenant `date`. La semaine française commence le
 * lundi ; démarrer la grille « aujourd'hui » ferait des bornes qui changent
 * chaque jour, et une semaine qu'on ne reconnaît pas d'un coup d'œil.
 *
 * Exporté pour les tests, qui construisent leurs repas sur la semaine
 * réellement affichée plutôt que sur une date figée qui périmerait.
 */
export function mondayOf(date: Date): string {
  const copy = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const weekday = copy.getUTCDay();
  copy.setUTCDate(copy.getUTCDate() - (weekday === 0 ? 6 : weekday - 1));
  return copy.toISOString().slice(0, 10);
}

function addDays(isoDay: string, days: number): string {
  const date = new Date(`${isoDay}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** « lundi 10 août », lisible sans calcul. */
function dayLabel(isoDay: string): string {
  const date = new Date(`${isoDay}T00:00:00Z`);
  const name = DAY_NAMES[date.getUTCDay()] ?? '';
  return `${name} ${date.getUTCDate()}`;
}

export function MealPlan(): React.JSX.Element {
  const [start, setStart] = useState(() => mondayOf(new Date()));
  const [meals, setMeals] = useState<PlannedMeal[] | null>(null);
  const [recipes, setRecipes] = useState<RecipeListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    listMealPlan(start, controller.signal)
      .then((data) => { setMeals(data); setError(null); })
      .catch((e: unknown) => {
        if (e instanceof Error && e.name !== 'AbortError') setError(e.message);
      });
    return () => { controller.abort(); };
  }, [start]);

  useEffect(() => {
    const controller = new AbortController();
    listRecipes('', { signal: controller.signal, sort: 'alpha' })
      .then(setRecipes)
      .catch(() => { /* sans liste, la grille reste lisible : seule l'affectation est indisponible */ });
    return () => { controller.abort(); };
  }, []);

  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(start, index)), [start]);
  const bySlot = useMemo(() => {
    const map = new Map<string, PlannedMeal>();
    for (const meal of meals ?? []) map.set(`${meal.date} ${meal.meal}`, meal);
    return map;
  }, [meals]);

  function assign(date: string, meal: MealSlot, recipeId: string): void {
    setError(null);
    const action = recipeId === '' ? clearMeal(date, meal) : setMeal({ date, meal, recipe_id: recipeId });
    action
      .then(() => listMealPlan(start))
      .then(setMeals)
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); });
  }

  function toShopping(): void {
    setBusy(true);
    setError(null);
    planWeekToShopping(start)
      .then(() => { setNotice('Les ingrédients de la semaine sont dans votre liste de courses.'); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setBusy(false); });
  }

  const plannedCount = meals?.length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      {/* Écran de premier niveau : plus de « ← Bibliothèque », qui proposait
          de remonter à un parent inexistant. Voir Journal.tsx. */}
      <PageHeader
        title="Semaine"
        lede="Décidez ce que vous mangez, puis envoyez les ingrédients dans la liste de courses."
        actions={
          <>
            <Button type="button" variant="quiet" size="compact" onClick={() => { setStart(addDays(start, -7)); }}>
              <Icon name="fleche-gauche" size={0.9} /> Semaine précédente
            </Button>
            <Button type="button" variant="quiet" size="compact" onClick={() => { setStart(addDays(start, 7)); }}>
              Semaine suivante <Icon name="fleche-droite" size={0.9} />
            </Button>
          </>
        }
      />

      {error !== null && <Banner tone="danger">{error}</Banner>}
      {notice !== null && (
        <Banner tone="success">
          {notice} <Link to={paths.shopping} className="underline">Voir la liste</Link>
        </Banner>
      )}

      {/*
        **Sept colonnes sur grand écran** (refonte éditoriale).

        C'était sept lignes empilées, chacune une carte pleine largeur portant
        deux listes déroulantes côte à côte — soit, sur un écran de 1440 px,
        une colonne de 630 px et une page de 900 px de haut pour montrer
        quatorze créneaux. Or une semaine ne se lit pas ligne à ligne : on la
        regarde d'un bloc pour voir où sont les trous. Une liste verticale
        interdit exactement le geste que cet écran sert à faire.

        Sept colonnes au-dessus de `xl`, quatre à `lg`, deux à `sm`, une sur
        téléphone — où l'empilement reste juste, le pouce faisant défiler de
        toute façon. Midi et soir empilés dans le jour plutôt que côte à côte :
        c'est l'axe des jours qu'on parcourt, celui des repas se lit sur place.

        `items-start` sur la grille : un jour dont le créneau porte un titre
        long ne doit pas étirer les six autres.
      */}
      <ul
        data-testid="meal-plan"
        className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7"
      >
        {days.map((day) => (
          <li key={day} className="h-full">
            <Card className="flex h-full flex-col gap-3">
              <p className="text-label">{dayLabel(day)}</p>
              <div className="flex flex-col gap-3">
                {MEAL_SLOTS.map((slot) => {
                  const planned = bySlot.get(`${day} ${slot}`);
                  return (
                    <div key={slot} className="flex flex-col gap-1">
                      <label className="flex flex-col gap-1 text-sm">
                        <span className="text-foreground-secondary">{SLOT_LABEL[slot]}</span>
                        <Select
                          density="compact"
                          aria-label={`${SLOT_LABEL[slot]} — ${dayLabel(day)}`}
                          value={planned?.recipe_id ?? ''}
                          onChange={(e) => { assign(day, slot, e.target.value); }}
                        >
                          <option value="">—</option>
                          {recipes.map((recipe) => (
                            <option key={recipe.id} value={recipe.id}>{recipe.title}</option>
                          ))}
                        </Select>
                      </label>
                      {planned !== undefined && (
                        <p className="flex flex-wrap items-center gap-2 text-meta">
                          <Link to={paths.recipe(planned.recipe_id)} className="underline">
                            Voir la recette
                          </Link>
                          {planned.total_min !== null && <span className="text-data">{planned.total_min} min</span>}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          </li>
        ))}
      </ul>

      {/* Le bouton qui referme la boucle : ce qu'on a décidé devient ce qu'il
          faut acheter (US-28). */}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="primary" disabled={plannedCount === 0 || busy} onClick={toShopping}>
          Ajouter la semaine à ma liste de courses
        </Button>
        {plannedCount === 0 && (
          <p className="text-sm text-foreground-muted">Choisissez au moins un repas.</p>
        )}
      </div>
    </div>
  );
}
